import { Spinner } from "@chakra-ui/react";
import {
	postAddendumAnswer,
	postAddendumQuestion,
} from "actions/RFQ/addendumAction";
import { Icon } from "assets/Icon";
import { FormattedInput } from "components/FormInputs/FormattedInput";
import { Avatar } from "components/RFQ/Avatar";
import { Button } from "newui/components/Button";
import { CustomModal } from "components/RFQ/Modal/CustomModal";
import { Form, Formik } from "formik";
import dayjs from "newui/utils/dayjs";
import React, { useState } from "react";
import { cn } from "utils/helpers";
import { AcceptedFileTypesForRFxWithImage } from "utils/rfxLargeFileUploadHelper";
import { FormikFileUpload } from "newui/components/FileUpload";
import { useCurrentCompanyValue } from "hooks/auth";
import { useFileUploadContext } from "newui/components/FileUpload/hooks/FileUploadContext";
import useRefreshWarning from "hooks/useRefreshWarning";

const AddendumMessage = ({
	nested = false,
	logo,
	rfqId,
	setAddendumData,
	questionId,
	indexList,
	...props
}) => {
	const { isUploading } = useFileUploadContext();
	const [published, setPublished] = React.useState(nested ? false : true);
	const [isSending, setIsSending] = React.useState(false);
	const [isOpen, setIsOpen] = React.useState(false);
	const companyLogo = useCurrentCompanyValue('logo_path');
	const [hasAttachment, setHasAttachment] = useState(false);
	const setErrors = () => { };
	const onMaxCharacter = () => { };
	useRefreshWarning

	const handleAddQuestion = async (values, resetForm) => {
		setIsSending(true);
		postAddendumQuestion({
			rfq_id: rfqId,
			addendum: values.input,
			attachments: values.attachments,
		}).then((res) => {
			if (res) {
				setAddendumData((prev) => {
					let newAddendum = [...prev];
					if (
						newAddendum.length < 1 ||
						dayjs(newAddendum[0]?.date).diff(dayjs({ hours: 0 }), "days") < 0
					) {
						newAddendum.unshift({ date: dayjs().format(), question: [res] });
					} else {
						newAddendum[0].question.unshift(res);
					}
					return newAddendum;
				});
				resetForm();
				setHasAttachment(false);
				setIsOpen(false);
			}
		}).finally(() => {
			setIsSending(false);
		});
	};

	const handleAddAnswer = async (values, resetForm) => {
		const data = {
			rfq_id: rfqId,
			addendum: values.input,
			attachments: values.attachments,
			published: published ? 1 : 0,
			rfq_addendum_question_id: questionId,
		};
		setIsSending(true);
		postAddendumAnswer(data).then((res) => {
			if (res) {
				setAddendumData((prev) => {
					let newAddendum = [...prev];
					if (published) {
						newAddendum.forEach((addendum, i) => {
							addendum.question = addendum.question.filter(
								(question) => question.id !== questionId
							);
						});
					} else {
						newAddendum[indexList.addendum].question[
							indexList.question
						].published = published;
						newAddendum[indexList.addendum]?.question[
							indexList.question
						]?.answer.unshift(res);
					}

					return newAddendum;
				});
				resetForm();
				setHasAttachment(false);
				setIsOpen(false);

				props?.handleClose && props.handleClose();
			}
		}).finally(() => {
			setIsSending(false);
		});
	};

	const handleSubmitForm = async (values, { resetForm }) => {
		if (nested) {
			handleAddAnswer(values, resetForm);
		} else {
			handleAddQuestion(values, resetForm);
		}
	};

	const dialogProperty = {
		icon: "check-circle-blue",
		bgColor: "bg-brand-secondary",
		iconColor: "text-utility-brand-700",
		title: `Send ${nested ? "Answer" : "Addendum"}`,
		description: nested
			? "You’re about to respond to a Suppliers query. Please review your details carefully before sending.  "
			: "You’re about to send this addendum to all tenderers. Please review the details carefully to ensure the information is accurate and complete before proceeding.",
	};

	return (
		<div
			className={cn("items-start pb-1", nested && "pl-10 pr-10")}
			{...props}
		>
			<div className="flex items-start justify-start gap-3">
				<Avatar
					avatar={companyLogo}
					noHover
				/>
				<div style={{ width: "100%" }}>
					<Formik
						initialValues={{ input: "", attachments: [] }}
						onSubmit={handleSubmitForm}
						style={{ width: "100%" }}
					>
						{({ values, isValid, submitForm }) => {
							const FooterModal = () => {
								return (
									<div className="flex w-full justify-between gap-2 text-utility-error-50">
										<Button
											onClick={() => submitForm()}
											className="!w-full"
											variant="primary"
										>
											{isSending ? <Spinner /> : "Send"}
										</Button>
										<Button
											type="button"
											onClick={() => setIsOpen(false)}
											className="!w-full"
											btntype="base"
											disabled={isSending}
										>
											Cancel
										</Button>
									</div>
								);
							};
							return (
								<Form>
									<div className="relative">
										<div className="relative">
											<FormattedInput
												label=""
												name="input"
												type="text"
												placeholder={nested ? "Add a reply" : "Add an addendum"}
												characterLimit={1500}
												className="col xl-12"
												background="white"
												setErrors={setErrors}
												onMaxCharacter={onMaxCharacter}
												guideLine={
													"Tell us about this project and your company's role"
												}
												setFieldError={() => { }}
											/>

											<div className="absolute bottom-0 left-0 right-0 mx-2 mb-1 bg-white p-3 pt-0">
												<div className="flex items-center justify-between">
													<button
														type="button"
														className="pointer"
														onClick={() => setHasAttachment(!hasAttachment)}
													>
														<Icon
															icon="paperclip"
															style={{ height: 18, width: 18 }}
														/>
													</button>

													<div className="ml-auto flex items-center justify-end">
														<Button
															isLoading={isUploading}
															type="button"
															btntype="primary"
															className="!h-10 !rounded-lg"
															onClick={() => setIsOpen(true)}
															disabled={
																values?.input?.trim() === "" ||
																!isValid ||
																isUploading
															}
														>
															<p className="font-roboto font-semibold">Send</p>
														</Button>
													</div>
												</div>
											</div>
										</div>
										{hasAttachment && (
											<div>
												<FormikFileUpload uploadType="addendum" maxFileSizeMB={100} name="attachments" allowFileTypes={AcceptedFileTypesForRFxWithImage} multiple supportsHardDelete />
											</div>
										)}
									</div>
									<CustomModal
										header={dialogProperty}
										isOpen={isOpen}
										onClose={() => setIsOpen(false)}
										footerComponent={<FooterModal />}
									/>
								</Form>
							);
						}}
					</Formik>
				</div>
			</div>
		</div>
	);
};

export default AddendumMessage;
